import React, { FC, useCallback, useState } from 'react';
import Button from 'components/antd/Button';
import { Form, Modal, Input, Select } from 'antd';
import { useMutation } from 'react-query';

import { GiftOutlined } from '@ant-design/icons';
import { requiredRule } from 'config/form';
import { ICustomMap } from 'types/ICustomMap';
import { textsCommon } from 'config/texts';
import { createUserFeedback } from 'pages/CommandCenter/UserFeedback/services';
import { useSelector } from 'react-redux';
import ButtonsRow from '../button/ButtonsRow';
import Message from '../Message';
import { MessageTypes } from '../Message/config';

const { TextArea } = Input;

type TProps = {
    iconOnly?: boolean;
};

const FeatureRequest: FC<TProps> = ({ iconOnly }) => {
    const [open, setOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const { id_str: accountId } = useSelector(({ workspace }: ICustomMap) => workspace);

    const { mutate: reqCreateUserFeedback, isLoading } = useMutation(createUserFeedback, {
        onSuccess: () => {
            setSuccess(true);
        },
    });

    const toggleOpen = useCallback(() => {
        setOpen(prev => !prev);
        setSuccess(false);
    }, []);

    const handleSubmit = useCallback(
        (values: ICustomMap) => {
            reqCreateUserFeedback({
                text: values.feature,
                feedback_type: 'FEATURE_REQUEST',
                app_state: 'Feature',
                company_id: accountId,
                title: values.title,
                category: values.category,
            });
        },
        [reqCreateUserFeedback, accountId],
    );
    return (
        <>
            <Button type='primary' onClick={toggleOpen}>
                {iconOnly ? <GiftOutlined /> : 'Request a Feature'}
            </Button>

            <Modal
                open={open}
                onCancel={isLoading ? undefined : toggleOpen}
                footer={null}
                title={success ? undefined : 'Request a Feature'}
            >
                {success ? (
                    <>
                        <Message
                            title='Thank You!'
                            description='Your feature request has been submitted successfully. Our team will review your suggestion and consider it for future updates.'
                            buttonText='Close'
                            onClick={toggleOpen}
                            buttonSize='large'
                            type={MessageTypes.Success}
                        />
                    </>
                ) : (
                    <>
                        <p>
                            We&apos;re constantly improving our app based on user feedback. Let us know what feature you&apos;d like to see
                            next!
                        </p>
                        <Form layout='vertical' onFinish={handleSubmit}>
                            <Form.Item name='title'>
                                <Input placeholder='Feature Title' disabled={isLoading} />
                            </Form.Item>
                            <Form.Item name='category' rules={[requiredRule]}>
                                <Select placeholder='Select Category' disabled={isLoading}>
                                    <Select.Option value='Access Control'>Access Control</Select.Option>
                                    <Select.Option value='Notification'>Notification</Select.Option>
                                    <Select.Option value='User Interaction'>User Interaction</Select.Option>
                                    <Select.Option value='Integration'>Integration</Select.Option>
                                    <Select.Option value='Other'>Other</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name='feature' rules={[requiredRule]}>
                                <TextArea placeholder='Feature Description' autoSize={{ minRows: 4, maxRows: 12 }} disabled={isLoading} />
                            </Form.Item>
                            <ButtonsRow>
                                <Form.Item noStyle>
                                    <Button type='primary' htmlType='submit' loading={isLoading}>
                                        {textsCommon.buttons.submit}
                                    </Button>
                                </Form.Item>
                                <Button htmlType='button' onClick={toggleOpen} disabled={isLoading}>
                                    {textsCommon.buttons.cancel}
                                </Button>
                            </ButtonsRow>
                        </Form>
                    </>
                )}
            </Modal>
        </>
    );
};

export default FeatureRequest;
